import moment from 'moment';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import React, { useState } from 'react';
import { DISPLAY_DATE_FORMAT_LONG, INPUT_DATE_FORMAT_SHORT } from '../../../config/helpers';
import { linkPropTypes } from '../../../propTypes';
import AbandonBasketModal, { abandonBasketModalLabelProps } from '../AbandonBasketModal';
import Button from '../Atoms/Button';
import StickyComponent from '../StickyComponent';
import useCurrency from '../../../hooks/useCurrency';
import { currencyWithPriceAndFormattedPriceArrayPropTypes } from '../../../util/currency';

const propTypes = exact({
  packageHeaderTitle: PropTypes.string,
  packageHeaderSubtitle: PropTypes.string,
  displayAtolProtectedText: PropTypes.bool,
  atolProtectedText: PropTypes.string,
  fromDate: PropTypes.string,
  toDate: PropTypes.string,
  fromPriceText: PropTypes.string,
  pricePerCurrency: currencyWithPriceAndFormattedPriceArrayPropTypes,
  isCallToBook: PropTypes.bool,
  callToBookButtonText: PropTypes.string,
  isSoldOut: PropTypes.bool,
  soldOutButtonText: PropTypes.string,
  bookNowCta: linkPropTypes,
  waitingListCta: linkPropTypes,
  abandonBasketEnabled: PropTypes.bool,
  abandonBasketModal: abandonBasketModalLabelProps,
  abandonBasketApi: PropTypes.string.isRequired,
  tourKey: PropTypes.string.isRequired,
  perPersonText: PropTypes.string,
});

const defaultProps = {
  packageHeaderTitle: '',
  packageHeaderSubtitle: '',
  displayAtolProtectedText: false,
  atolProtectedText: '',
  fromDate: '',
  toDate: '',
  fromPriceText: '',
  perPersonText: 'pp',
  pricePerCurrency: [],
  isCallToBook: false,
  callToBookButtonText: '',
  isSoldOut: false,
  soldOutButtonText: '',
  bookNowCta: undefined,
  waitingListCta: undefined,
  abandonBasketEnabled: false,
  abandonBasketModal: undefined,
};

function PackageCta({
  packageHeaderTitle,
  packageHeaderSubtitle,
  displayAtolProtectedText,
  atolProtectedText,
  fromDate,
  toDate,
  fromPriceText,
  pricePerCurrency,
  isCallToBook,
  callToBookButtonText,
  isSoldOut,
  soldOutButtonText,
  bookNowCta,
  waitingListCta,
  abandonBasketEnabled,
  abandonBasketModal,
  abandonBasketApi,
  tourKey,
  perPersonText,
}) {
  const [isAbandonBasketModalOpen, setIsAbandonBasketModalOpen] = useState(false);
  const [currentCurrencyCodeAndSymbol, currencyPriceSetting] = useCurrency(pricePerCurrency);

  function onClickBookNow() {
    if (abandonBasketEnabled) {
      setIsAbandonBasketModalOpen(true);
    } else {
      window.location.href = bookNowCta.url;
    }
  }

  function renderButton() {
    let button;
    if (isCallToBook) {
      button = <span className="PackageCta_callToBook">{callToBookButtonText}</span>;
    } else if (waitingListCta) {
      button = (
        <Button
          blockName="PackageCta_button"
          elementType="link"
          modifier="fill"
          text={waitingListCta.title}
          url={waitingListCta.url}
        />
      );
    } else if (isSoldOut) {
      button = <span className="PackageCta_soldOut">{soldOutButtonText}</span>;
    } else {
      button = (
        <Button
          blockName="PackageCta_button"
          modifier="fill"
          text={bookNowCta.title}
          handleClick={() => onClickBookNow()}
        />
      );
    }
    return button;
  }

  return (
    <>
      <StickyComponent>
        <div className="PackageCta">
          <div className="PackageCta_body">
            <div className="PackageCta_column">
              <div className="PackageCta_overview">
                <div className="PackageCta_content">
                  <div className="PackageCta_overviewContent">
                    <h3 className="PackageCta_title">{packageHeaderTitle}</h3>
                    <span className="PackageCta_time">
                      {`${moment(fromDate).format(DISPLAY_DATE_FORMAT_LONG)} to ${moment(
                        toDate
                      ).format(DISPLAY_DATE_FORMAT_LONG)}`}
                    </span>
                    <span className="PackageCta_time-short">
                      {`${moment(fromDate).format(INPUT_DATE_FORMAT_SHORT)} - ${moment(
                        toDate
                      ).format(INPUT_DATE_FORMAT_SHORT)}`}
                    </span>
                    {packageHeaderSubtitle && (
                      <span className="PackageCta_subTitle">
                        <span className="PackageCta_textDivider">&nbsp;/&nbsp;</span>
                        {packageHeaderSubtitle}
                      </span>
                    )}
                    {displayAtolProtectedText && (
                      <span className="PackageCta_subTitle">
                        <span className="PackageCta_textDivider">&nbsp;/&nbsp;</span>
                        {atolProtectedText}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="PackageCta_price">
                <div className="PackageCta_content">
                  <div className="PackageCta_priceContent">
                    <span className="PackageCta_sup">{fromPriceText}</span>
                    <span className="PackageCta_perPerson">
                      {currentCurrencyCodeAndSymbol.symbol}
                      {currencyPriceSetting.formattedPrice}
                      {perPersonText}
                    </span>
                  </div>
                </div>
              </div>
              <div className="PackageCta_book">
                <div className="PackageCta_content">
                  <div className="PackageCta_form">{renderButton()}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </StickyComponent>
      {abandonBasketEnabled && (
        <AbandonBasketModal
          bookNowUrl={bookNowCta.url}
          isOpen={isAbandonBasketModalOpen}
          labels={abandonBasketModal}
          onClose={() => {
            setIsAbandonBasketModalOpen(false);
          }}
          submitApi={abandonBasketApi}
          tourKey={tourKey}
        />
      )}
    </>
  );
}

PackageCta.propTypes = propTypes;
PackageCta.defaultProps = defaultProps;

export default PackageCta;
